/*
 * Copyright (c) Nexign, JSC, 2019-2025.
 */

/**
 * Guard constants
 *
 * @author Yakov Gushchin
 * @author Misha Kokoulin
 * @author Maxim Fedorov
 * @author Anton Panasenko
 * @author Evgenij Tyan
 * @author Aleksandr Perkhunov
 * @author Julia Ignatieva
 * @author Dmitriy Kvon
 * @author Ilya Babanov
 * @author Evgeniy Naumov
 * @author Alexander Golshtein
 * @author Ilya Baranchikov
 * @author Nikolay Sviridov
 * @version 1.39.0
 * @since 1.0.0
 */

import { guardComponentType, IGuardReducer } from '@modules/role/reducer/guardReducer';
import { IConnectionSource } from '@modules/role/types/guardTypes';

export const ConnectionSourceEnum: IConnectionSource = {
    route: 'ROUTE',
    component: 'COMPONENT',
};

export enum AccessComponentKeyEnum {
    // Features
    Campaigns = 'Campaigns',
    CampaignsAlternative = 'CampaignsAlternative',
    columnResize = 'columnResize',
    DensityListView = 'densityListView',
    endUserDeleteBulk = 'endUserDeleteBulk',
    inquiries = 'inquiries',
    otherInquiries = 'otherInquiries',
    LabelsImportButton = 'labelsImportButton',
    multiBalances = 'multiBalances',
    onboarding = 'onboarding',
    personalAccountDisablingWarning = 'personalAccountDisablingWarning',
    personalDataConfirmCheck = 'personalDataConfirmCheck',
    Settings = 'settings',
    surveyMedallia = 'surveyMedallia',
    wizardChangeLockHideCost = 'wizardChangeLockHideCost',
    reportsConstructor = 'reportsConstructor',
    collectiveProduct = 'collectiveProduct',
    megaPowers = 'megaPowers',
    notifications = 'notifications',

    FooterAccount = 'FooterAccount',
    FooterHistoryInquiry = 'FooterHistoryInquiry',
    FooterHistoryOrder = 'FooterHistory',
    FooterOffers = 'FooterOffers',
    FooterProducts = 'FooterProducts',
    FooterServicesSubscriberMobile = 'FooterServicesSubscriberMobile',

    // Pages
    PageAccountCashback = 'PageAccountCashback',
    PageAccountInfo = 'PageAccountInfo',
    PageAccountInfoFinances = 'PageAccountInfoFinances',
    PageAccountInfoGeneral = 'PageAccountInfoGeneral',
    PageAccountInfoShipping = 'PageAccountInfoShipping',
    PageAutoReportId = 'PageAutoReportId',
    PageBills = 'PageBills',
    PageCampaign = 'PageCampaign',
    PageCampaignInfo = 'PageCampaignInfo',
    PageCollectiveProduct = 'PageCollectiveProduct',
    PageDashboard = 'PageDashboard',
    PageExpensesAnalyzer = 'PageExpensesAnalyzer',
    PageForbidden = 'PageForbidden',
    PageHistoryInquiry = 'PageHistoryInquiry',
    PageHistoryOrder = 'PageHistoryOrder',
    PageInquiryInfo = 'PageInquiryInfo',
    PageNotFound = 'PageNotFound',
    PageNews = 'PageNews',
    PageOffers = 'PageOffers',
    PageOrderId = 'PageOrderId',
    PagePayments = 'PagePayments',
    PagePersonalAccount = 'PagePersonalAccount',
    PageProducts = 'PageProducts',
    PageReportsConstructor = 'PageReportsConstructor',
    PageReportsSubscriber = 'PageReportsSubscriber',
    PageSettings = 'PageSettings',
    PageSettingsAutoReports = 'PageSettingsAutoReports',
    PageSettingsLabels = 'PageSettingsLabels',
    PageSettingsPositions = 'PageSettingsPositions',
    PageSettingsStatistics = 'PageSettingsStatistics',
    PageSubscriberId = 'PageSubscriberId',
    PageSubscriberMobile = 'PageSubscriberMobile',
    FastInquiryCreate = 'FastInquiryCreate',
    FastAdvanceInvoice = 'FastAdvanceInvoice',
    FastPromisedPayment = 'FastPromisedPayment',

    // Wizards
    PageLabelsImport = 'PageLabelsImport',
    PageMoneyTransfer = 'PageMoneyTransfer',
    PageWizardAdditionalAccounts = 'PageWizardAdditionalAccounts',
    PageWizardBillsDocs = 'PageWizardBillsDocs',
    PageWizardCallsDetail = 'PageWizardCallsDetail',
    PageWizardChangeLock = 'PageWizardChangeLock',
    PageWizardExpensesDetail = 'PageWizardExpensesDetail',
    PageWizardImportPersonalData = 'PageWizardImportPersonalData',
    PageWizardMultiSearch = 'PageWizardMultiSearch',
    PageWizardOrderTemplate = 'PageWizardOrderTemplate',
    PageWizardOrderReport = 'PageWizardOrderReport',
    PageWizardCollationReport = 'PageWizardCollationReport',
    TitleButtonWizardMultiSearch = 'TitleButtonWizardMultiSearch',
    TableMsisdnMultiSearch = 'TableMsisdnMultiSearch',
    WizardProductOfferings = 'WizardProductOfferings',
    PageWizardAdditionalReports = 'PageWizardAdditionalReports',
    PageWizardImportSubscribersLimits = 'PageWizardImportSubscribersLimits',
    WizardFastBillsDocs = 'WizardFastBillsDocs',
    WizardFastReportDocs = 'WizardFastReportDocs',
    PageWizardChangeRole = 'PageWizardChangeRole',

    TabMenuAccount = 'TabMenuAccount',
    TabMenuHistory = 'TabMenuHistory',
    TabMenuHistoryInquiry = 'TabMenuHistoryInquiry',
    TabMenuHistoryOrder = 'TabMenuHistoryOrder',
    TabMenuMain = 'TabMenuMain',
    TabMenuOffers = 'TabMenuOffers',
    TabMenuProducts = 'TabMenuProducts',
    TabMenuProductsCashback = 'TabMenuProductsCashback',
    TabMenuProductsOnly = 'TabMenuProductsOnly',
    TabMenuService = 'TabMenuService',
    TabMenuServicesSubscriber = 'TabMenuServicesSubscriber',
    TabMenuNews = 'TabMenuNews',

    // Menu items of PageSettings
    PageSettingsMenuAutoReports = 'PageSettingsMenuAutoReports',
    PageSettingsMenuLabels = 'PageSettingsMenuLabels',
    PageSettingsMenuPositions = 'PageSettingsMenuPositions',
    PageSettingsMenuStatistics = 'PageSettingsMenuStatistics',

    // Title buttons
    TitleButtonPersonalDataDelete = 'PERSONAL_DATA_BULK_DELETE',

    // footer buttons
    FooterButtonAccountCompare = 'ACCOUNT_COMPARE',
    FooterButtonBalanceUpdate = 'BALANCE_UPDATE',
    FooterButtonBills = 'BILLS',
    FooterButtonBillsDoc = 'BILLS_GET_DOC',
    FooterButtonDcruleList = 'DCRULE_LIST',
    FooterButtonExpenses = 'EXPENSES',
    FooterButtonInquiryCreate = 'INQUIRY_CREATE',
    FooterButtonInvoiceRepayment = 'INVOICE_PREPAYMENT',
    FooterButtonPayment = 'PAYMENTS',
    FooterButtonReconciliation = 'ACT_RECONCILIATION',
    FooterButtonReports = 'REPORTS',
    FooterButtonMoneyTransfer = 'MONEY_TRANSFER',

    FooterButtonAccessLevels = 'ACCESS_LEVELS',
    FooterButtonAdditionalAccount = 'ADDITIONAL_ACCOUNTS',
    FooterButtonAdditionalReports = 'ADDITIONAL_REPORTS',
    FooterButtonCallDetails = 'CALL_DETAILS',
    FooterButtonDcruleChange = 'DCRULE_CHANGE',
    FooterButtonLabels = 'LABELS',
    FooterButtonLock = 'LOCK',
    FooterButtonRateServicesAndTariffOptions = 'RATE_SERVICES_AND_TARIFF_OPTIONS',
    FooterButtonTariffChange = 'TARIFF_CHANGE',

    FooterButtonShowDailyExpenses = 'SHOW_DAILY_EXPENSES',
    FooterButtonShowOnGraph = 'PLOT_GRAPH',
    FooterButtonShowSubsExpenses = 'SHOW_SUBS_EXPENSES',
    FooterButtonWizardExpensesDetail = 'DETAIL_CLIENT_EXPENSES',
}

export enum FeatureNameEnum {
    AUTO_REPORT_ID = 'autoReportIdNewUI',
    BROWSER_WARNING = 'unsupportedBrowserWarning',
    CASHBACK = 'cashBack',
    COLUMN_RESIZE = 'columnResize',
    END_USER_DELETE_BULK = 'endUserDeleteBulk',
    INQUIRIES = 'inquiries',
    OTHER_INQUIRIES = 'otherInquiries',
    INQUIRY_INFO = 'inquiryInfoNewUI',
    MULTI_BALANCES = 'multiBalances',
    ONBOARDING = 'onboarding',
    ORDER_ID = 'orderIdNewUI',
    PAYMENTS = 'paymentsNewUI',
    PERSONAL_ACCOUNT_DISABLE_WARNING = 'personalAccountDisablingWarning',
    PERSONAL_DATA_CONFIRM_CHECK = 'personalDataConfirmCheck',
    PRODUCTS = 'products',
    SURVEY_MEDALLIA = 'surveyMedallia',
    WIZARD_CHANGE_LOCK_HIDE_COST = 'changeLockHideCost',
    WIZARD_MULTI_SEARCH = 'subsMultiSearch',
    REPORTS_CONSTRUCTOR = 'reportsConstructor',
    MEGA_POWERS = 'megaPowers',
    NEWS = 'news',
    CHANGES_CALCULATING_REMAINS_AND_RATE_PLAN = 'changesCalculatingRemainsAndRatePlan',
    EXPENSES_PRELIMINARY_DATA = 'expensesPreliminaryData',
    CDM_ADDITIONAL_REPORTS = 'cdmAdditionalReports',
    SEND_REPORTS_NOTIFICATIONS_VIA_CNC = 'sendReportsNotificationsViaCnc',
    CAMPAIGNS_IMPROVEMENT = 'campaignsImprovement',
    IMPORT_SUBSCRIBERS_LIMITS = 'importSubscribersLimits',
    BILLING_DELIVERY_EMAIL_INQUIRY = 'billingDeliveryEmailInquiry',
    BILLING_DELIVERY_EMAIL_SEARCH_INQUIRIES = 'billingDeliveryEmailSearchInquiries',
    CVM_NOTIFICATIONS = 'cvmNotifications',
    SUBSCRIBERS_IN_ROAMING_NOTIFICATION = 'subscribersInRoamingNotification',
    CHECK_PERSONAL_ACCOUNT_BALANCE_BEFORE_CLOSING = 'checkPersonalAccountBalanceBeforeClosing',
    ROAMING_PRODUCTS = 'roamingProducts',
    CASHBACK_NOTIFICATIONS = 'cashbackNotifications',
    MAIN_OPERATIONS = 'mainOperations',
    CHANGE_ROLE_NEW_UI = 'changeRoleNewUi',
    DCHAT = 'dchat',
    INSTRUCTIONS_LINKS = 'instructionsLinks',
    CAMPAIGN_RATEPLAN_CARD_2025 = 'campaignRateplanCard2025'
}

export enum EGuardAuthUserFlag {
    IS_SUBSCRIBER = 'isSubscriber',
}

export const initialGuardComponents: guardComponentType = {
    [AccessComponentKeyEnum.TitleButtonPersonalDataDelete]: true,
    [AccessComponentKeyEnum.TitleButtonWizardMultiSearch]: true,
    [AccessComponentKeyEnum.TableMsisdnMultiSearch]: true,

    [AccessComponentKeyEnum.FooterButtonAccountCompare]: true,
    [AccessComponentKeyEnum.FooterButtonBalanceUpdate]: true,
    [AccessComponentKeyEnum.FooterButtonBills]: true,
    [AccessComponentKeyEnum.FooterButtonBillsDoc]: true,
    [AccessComponentKeyEnum.FooterButtonDcruleList]: true,
    [AccessComponentKeyEnum.FooterButtonExpenses]: true,
    [AccessComponentKeyEnum.FooterButtonInquiryCreate]: true,
    [AccessComponentKeyEnum.FooterButtonInvoiceRepayment]: true,
    [AccessComponentKeyEnum.FooterButtonPayment]: true,
    [AccessComponentKeyEnum.FooterButtonReconciliation]: true,
    [AccessComponentKeyEnum.FooterButtonReports]: true,
    [AccessComponentKeyEnum.FooterButtonMoneyTransfer]: true,

    [AccessComponentKeyEnum.FooterButtonShowOnGraph]: true,
    [AccessComponentKeyEnum.FooterButtonShowSubsExpenses]: true,
    [AccessComponentKeyEnum.FooterButtonShowDailyExpenses]: true,
    [AccessComponentKeyEnum.FooterButtonWizardExpensesDetail]: true,

    [AccessComponentKeyEnum.FooterButtonLock]: true,
    [AccessComponentKeyEnum.FooterButtonDcruleChange]: true,
    [AccessComponentKeyEnum.FooterButtonRateServicesAndTariffOptions]: true,
    [AccessComponentKeyEnum.FooterButtonLabels]: true,
    [AccessComponentKeyEnum.FooterButtonAccessLevels]: true,
    [AccessComponentKeyEnum.FooterButtonTariffChange]: true,
    [AccessComponentKeyEnum.FooterButtonAdditionalAccount]: true,
    [AccessComponentKeyEnum.FooterButtonAdditionalReports]: true,
    [AccessComponentKeyEnum.FooterButtonCallDetails]: true,

    [AccessComponentKeyEnum.Campaigns]: true,
    [AccessComponentKeyEnum.CampaignsAlternative]: true,
    [AccessComponentKeyEnum.columnResize]: false,
    [AccessComponentKeyEnum.DensityListView]: false,
    [AccessComponentKeyEnum.endUserDeleteBulk]: false,
    [AccessComponentKeyEnum.inquiries]: false,
    [AccessComponentKeyEnum.otherInquiries]: false,
    [AccessComponentKeyEnum.LabelsImportButton]: false,
    [AccessComponentKeyEnum.multiBalances]: false,
    [AccessComponentKeyEnum.onboarding]: false,
    [AccessComponentKeyEnum.personalAccountDisablingWarning]: false,
    [AccessComponentKeyEnum.personalDataConfirmCheck]: false,
    [AccessComponentKeyEnum.Settings]: false,
    [AccessComponentKeyEnum.surveyMedallia]: false,
    [AccessComponentKeyEnum.wizardChangeLockHideCost]: false,
    [AccessComponentKeyEnum.reportsConstructor]: false,
    [AccessComponentKeyEnum.collectiveProduct]: false,
    [AccessComponentKeyEnum.megaPowers]: false,
    [AccessComponentKeyEnum.notifications]: false,

    [AccessComponentKeyEnum.FooterAccount]: true,
    [AccessComponentKeyEnum.FooterHistoryInquiry]: true,
    [AccessComponentKeyEnum.FooterHistoryOrder]: true,
    [AccessComponentKeyEnum.FooterOffers]: true,
    [AccessComponentKeyEnum.FooterProducts]: true,
    [AccessComponentKeyEnum.FooterServicesSubscriberMobile]: true,

    // Pages
    [AccessComponentKeyEnum.PageAccountInfo]: true,
    [AccessComponentKeyEnum.PageAccountInfoFinances]: true,
    [AccessComponentKeyEnum.PageAccountInfoGeneral]: true,
    [AccessComponentKeyEnum.PageAccountInfoShipping]: true,
    [AccessComponentKeyEnum.PageAccountCashback]: true,
    [AccessComponentKeyEnum.PageAutoReportId]: true,
    [AccessComponentKeyEnum.PageBills]: true,
    [AccessComponentKeyEnum.PageCampaign]: true,
    [AccessComponentKeyEnum.PageCampaignInfo]: true,
    [AccessComponentKeyEnum.PageCollectiveProduct]: true,
    [AccessComponentKeyEnum.PageDashboard]: true,
    [AccessComponentKeyEnum.PageExpensesAnalyzer]: true,
    [AccessComponentKeyEnum.PageForbidden]: true,
    [AccessComponentKeyEnum.PageHistoryInquiry]: true,
    [AccessComponentKeyEnum.PageHistoryOrder]: true,
    [AccessComponentKeyEnum.PageInquiryInfo]: true,
    [AccessComponentKeyEnum.PageNotFound]: true,
    [AccessComponentKeyEnum.PageNews]: true,
    [AccessComponentKeyEnum.PageOffers]: true,
    [AccessComponentKeyEnum.PageOrderId]: true,
    [AccessComponentKeyEnum.PagePayments]: true,
    [AccessComponentKeyEnum.PagePersonalAccount]: true,
    [AccessComponentKeyEnum.PageProducts]: true,
    [AccessComponentKeyEnum.PageReportsConstructor]: true,
    [AccessComponentKeyEnum.PageReportsSubscriber]: true,
    [AccessComponentKeyEnum.PageSettings]: true,
    [AccessComponentKeyEnum.PageSettingsAutoReports]: true,
    [AccessComponentKeyEnum.PageSettingsLabels]: true,
    [AccessComponentKeyEnum.PageSettingsPositions]: true,
    [AccessComponentKeyEnum.PageSettingsStatistics]: false,
    [AccessComponentKeyEnum.PageSubscriberId]: true,
    [AccessComponentKeyEnum.PageSubscriberMobile]: true,
    [AccessComponentKeyEnum.FastInquiryCreate]: true,
    [AccessComponentKeyEnum.FastAdvanceInvoice]: true,
    [AccessComponentKeyEnum.FastPromisedPayment]: true,

    // Wizards
    [AccessComponentKeyEnum.PageLabelsImport]: true,
    [AccessComponentKeyEnum.PageMoneyTransfer]: true,
    [AccessComponentKeyEnum.PageWizardAdditionalAccounts]: true,
    [AccessComponentKeyEnum.PageWizardBillsDocs]: true,
    [AccessComponentKeyEnum.PageWizardCallsDetail]: true,
    [AccessComponentKeyEnum.PageWizardChangeLock]: true,
    [AccessComponentKeyEnum.PageWizardExpensesDetail]: true,
    [AccessComponentKeyEnum.PageWizardImportPersonalData]: true,
    [AccessComponentKeyEnum.PageWizardMultiSearch]: true,
    [AccessComponentKeyEnum.PageWizardOrderTemplate]: true,
    [AccessComponentKeyEnum.PageWizardOrderReport]: true,
    [AccessComponentKeyEnum.PageWizardCollationReport]: true,
    [AccessComponentKeyEnum.WizardProductOfferings]: true,
    [AccessComponentKeyEnum.PageWizardAdditionalReports]: true,
    [AccessComponentKeyEnum.PageWizardImportSubscribersLimits]: true,
    [AccessComponentKeyEnum.WizardFastBillsDocs]: true,
    [AccessComponentKeyEnum.WizardFastReportDocs]: true,
    [AccessComponentKeyEnum.PageWizardChangeRole]: true,

    // Menu items of PageSettings
    [AccessComponentKeyEnum.PageSettingsMenuAutoReports]: true,
    [AccessComponentKeyEnum.PageSettingsMenuLabels]: true,
    [AccessComponentKeyEnum.PageSettingsMenuPositions]: true,
    [AccessComponentKeyEnum.PageSettingsMenuStatistics]: false,

    [AccessComponentKeyEnum.TabMenuAccount]: true,
    [AccessComponentKeyEnum.TabMenuHistory]: true,
    [AccessComponentKeyEnum.TabMenuHistoryInquiry]: true,
    [AccessComponentKeyEnum.TabMenuHistoryOrder]: true,
    [AccessComponentKeyEnum.TabMenuMain]: true,
    [AccessComponentKeyEnum.TabMenuOffers]: true,
    [AccessComponentKeyEnum.TabMenuProducts]: true,
    [AccessComponentKeyEnum.TabMenuProductsOnly]: true,
    [AccessComponentKeyEnum.TabMenuProductsCashback]: true,
    [AccessComponentKeyEnum.TabMenuService]: true,
    [AccessComponentKeyEnum.TabMenuServicesSubscriber]: true,
    [AccessComponentKeyEnum.TabMenuNews]: true,
};

export const initialGuardState: IGuardReducer = {
    components: initialGuardComponents,
    error: '',
    isLoaded: false,
};
